/**
 * resolveInitialModule — pure decision for which module pane (if any) the
 * authenticated shell should have OPEN on its very first paint, given
 * everything the readiness gate already knows.
 *
 * It mirrors the steady-state precedence the app settles on anyway —
 *   URL match > server-persisted open pane > remembered (client storage) >
 *   the conversation's first module by position —
 * so PRE-SEEDING the pane authority with this result produces the SAME end
 * state MainLayout's URL guard + ChatBox's pane hydration would reach, just
 * before the first paint instead of after it (eliminating the Chat→module
 * flash on login / hard refresh). It introduces no new fallback policy and no
 * hardcoded "Chat": chat-only is the result solely when there are no modules,
 * the URL is an explicit chat view, the viewport is compact, or the user's
 * persisted pane was explicitly closed.
 *
 * Deliberately framework-free (no React, no network) so it is exhaustively
 * unit-testable.
 */
import { isSummaryModuleType } from '@/types/modules';
import type { Module } from '@/types/modules';

export interface PersistedPaneState {
  moduleId: string;
  /** Whether the pane was visible (open) in the user's persisted state. */
  isVisible: boolean;
}

export interface ResolveInitialModuleInput {
  /** URL `/mod/:moduleId` segment (null when the route carries no module). */
  moduleIdParam: string | null | undefined;
  /** URL `:view` segment — `'chat'` is the explicit chat-only route. */
  view: string | null | undefined;
  /** The active conversation's modules (from the conversation-detail snapshot). */
  modules: Module[];
  /** Server last-open pane (conversation panel `state.chatPane`), or null. */
  persistedPane: PersistedPaneState | null;
  /** `lastModule:<conversationId>` from local/session storage, or null. */
  rememberedModuleId: string | null;
  /** Compact/mobile viewport — never auto-opens a pane (it would cover chat). */
  isCompact: boolean;
}

export interface InitialModuleResolution {
  /** The module to seed into the pane authority (null = chat-only). */
  moduleId: string | null;
  /** Whether the pane should be visible on the first authenticated paint. */
  open: boolean;
}

export function resolveInitialModule(input: ResolveInitialModuleInput): InitialModuleResolution {
  const { moduleIdParam, view, modules, persistedPane, rememberedModuleId, isCompact } = input;

  // Compact/mobile mirrors ChatBox's hydration: it never auto-restores the pane
  // (it would cover the chat), so the first paint is chat-only.
  if (isCompact) return { moduleId: null, open: false };

  // An explicit chat-only URL means the user chose chat — never auto-open.
  if (view === 'chat') return { moduleId: null, open: false };

  const liveModules = modules.filter((m) => !m.archived && !isSummaryModuleType(m.type));
  // Plain boolean (NOT a type predicate) so a falsy result never narrows the
  // argument away — a non-live string id (e.g. a plugin moduleId) must remain a
  // string for the plugin check below.
  const isLive = (id: string | null | undefined): boolean =>
    Boolean(id) && liveModules.some((m) => m.id === id);

  // 1. A valid module already named in the URL wins (deep link / restored
  //    route). ADR 028: the URL is canonical for the active module.
  if (moduleIdParam && isLive(moduleIdParam)) return { moduleId: moduleIdParam, open: true };
  // Plugin panes render off the route (not from activeConversation.modules), so
  // they can't be validated here — trust an explicit plugin URL as-is.
  if (moduleIdParam && moduleIdParam.startsWith('plugin:')) return { moduleId: moduleIdParam, open: true };

  // 2. Server-persisted pane state — what the user last had open.
  if (persistedPane) {
    // An explicitly-closed pane ⇒ chat-only, regardless of whether the module
    // still exists. Returning a null moduleId (not the closed module's id) keeps
    // the seeded pane authority consistent with the chat-only URL Gate 6
    // canonicalizes to, and never auto-opens a module the user had dismissed.
    if (!persistedPane.isVisible) return { moduleId: null, open: false };
    // Open pane on a still-live module ⇒ restore it. If the module is gone,
    // fall through to the remembered / first-module fallback below.
    if (isLive(persistedPane.moduleId)) {
      return { moduleId: persistedPane.moduleId, open: true };
    }
  }

  // 3. Remembered (client storage), then the conversation's first module by
  //    position — the existing deterministic auto-open fallback.
  if (rememberedModuleId && isLive(rememberedModuleId)) return { moduleId: rememberedModuleId, open: true };

  const firstModule = [...liveModules].sort((a, b) => a.position - b.position)[0];
  if (firstModule) return { moduleId: firstModule.id, open: true };

  // 4. No modules in this conversation — chat.
  return { moduleId: null, open: false };
}
